// MediosInputSVGEditor.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { IconUpload } from '../../../../../Customiz3r/components/ToolSelectorTop/Icons.jsx';
import { useEditor } from '../../../../../../App.jsx';
import { showConfirm, showErrorAlert, confirmDelete, showAlert } from '../../../../../../components/HelperAlerts/HelpersAlerts.jsx';
import DOMPurify from 'dompurify';
import Icon from "@mdi/react";
import {mdiContentSaveOutline, mdiDeleteOutline} from '@mdi/js';
import { PlusIcon } from '@heroicons/react/20/solid'; // Asegúrate de importar los íconos necesarios

const MediosInputSVGEditor = () => {
    const sanitizeSVG = (svgString) => {
        return DOMPurify.sanitize(svgString, { USE_PROFILES: { svg: true } });
    };

    const [file, setFile] = useState(null);
    const [svg, setSVG] = useState(null);

    const medioInitialState = {
        svg: '',
        nombre: '',
        sharing_permissions: ''
    };
    const [medio, setMedio] = useState(medioInitialState);
    const [errors, setErrors] = useState({});
    const { stateEditor, setStateEditor } = useEditor();

    useEffect(() => {
        if (stateEditor?.editorID != null && stateEditor?.editorID !== 'add') {
            axios.get(`media/${stateEditor.editorID}/`)
                .then(response => {
                    if (response.data) {
                        const imageUrl = `data:image/svg+xml,${encodeURIComponent(response.data.svg)}`;
                        // response.data.svg = btoa(response.data.svg); // Esta línea no es necesaria si ya se maneja correctamente
                        response.data.svg = btoa(response.data.svg); // Asegurar que el SVG esté en base64 si es necesario
                        setMedio(response.data);
                        // console.log('response.data:', response.data);
                        setFile(imageUrl);
                        setSVG(response.data.svg); // Asegurar que el SVG esté en base64 si es necesario
                    }
                })
                .catch(error => {
                    console.error('Error fetching media:', error);
                    // Opcional: Mostrar un mensaje de error al usuario
                });
        }
    }, [stateEditor.editorID]);

    useEffect(() => {
        return () => {
            if (file) URL.revokeObjectURL(file);
        };
    }, [file]);

    const validate = () => {
        const newErrors = {};
        if (!medio.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio.';
        if (!medio.sharing_permissions) newErrors.sharing_permissions = 'Selecciona una opción.';
        if (!svg && !file) newErrors.svg = 'Debes subir un archivo SVG.';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChangeSVG = (e) => {
        const uploadedFile = e.target.files[0];

        if (uploadedFile) {
            if (uploadedFile.type !== 'image/svg+xml') {
                showErrorAlert({
                    title: 'Archivo no válido',
                    text: 'Por favor, sube un archivo en formato SVG.',
                    confirmButtonText: 'Entendido',
                });
                e.target.value = '';
                return;
            }

            const reader = new FileReader();
            reader.readAsDataURL(uploadedFile);

            if (medio.nombre === '') {
                setMedio((prevMedio) => ({
                    ...prevMedio,
                    nombre: uploadedFile.name.split('.')[0],
                }));
            }

            reader.onload = () => {
                const rawSVG = reader.result.split(',')[1];
                const decodedSVG = atob(rawSVG);
                const sanitizedSVG = sanitizeSVG(decodedSVG);
                const sanitizedBase64SVG = btoa(sanitizedSVG);

                // console.log('sanitizedBase64SVGaaaaaa:', sanitizedBase64SVG);
                setFile(URL.createObjectURL(uploadedFile));
                setSVG(sanitizedBase64SVG);
                setMedio((prevMedio) => ({
                    ...prevMedio,
                    svg: sanitizedBase64SVG,
                }));
            };

            reader.onerror = (error) => {
                console.error('Error reading the file:', error);
                // Opcional: Mostrar un mensaje de error al usuario
            };
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setMedio((prevMedio) => ({
            ...prevMedio,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!validate()) return;

        if (stateEditor?.editorID === 'add') {
            // Crear un nuevo medio
            axios.post(`media/`, {
                svg: svg,
                nombre: medio.nombre,
                sharing_permissions: medio.sharing_permissions,
                medio: medio // Asegurarse de incluir el objeto completo si la API lo requiere
            })
                .then(response => {
                    showConfirm({
                        title: 'Medio creado',
                        text: '¿Qué desea hacer ahora?',
                        icon: 'info',
                        showCancelButton: true,
                        showDenyButton: true,
                        confirmButtonText: 'Volver a la lista',
                        denyButtonText: 'Añadir otro medio',
                        cancelButtonText: 'Quedarse en el actual'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            // Acción para volver a la lista
                            setStateEditor({
                                ...stateEditor,
                                editor: false,
                                editorID: null,
                            });
                        } else if (result.isDenied) {
                            // Acción para añadir otro medio
                            setMedio(medioInitialState);
                            setSVG(null);
                            setFile(null);
                        } else if (result.isDismissed) {
                            // Acción para quedarse en el medio actual
                            setStateEditor({
                                ...stateEditor,
                                editor: true,
                                editorID: response.data.id,
                            });
                        }
                    });
                })
                .catch(error => {
                    console.error('Error creating medio:', error.response?.data?.error || error.message);
                    showErrorAlert({
                        text: error.response?.data?.error || 'Ocurrió un error al crear el medio.',
                        confirmButtonText: 'Entendido',
                    });
                });
        } else {
            // Modificar un medio existente
            axios.put(`media/${stateEditor.editorID}/`, {
                svg: svg,
                nombre: medio.nombre,
                sharing_permissions: medio.sharing_permissions,
                medio: medio // Asegurarse de incluir el objeto completo si la API lo requiere
            })
                .then(response => {
                    if (response.code === 'ERR_BAD_REQUEST') {
                        showErrorAlert({
                            text: response.response?.data?.error || 'Ocurrió un error al modificar el medio.',
                            confirmButtonText: 'Entendido',
                        });
                    } else {
                        showConfirm({
                            title: 'Medio modificado',
                            text: '¿Quiere volver a su perfil?',
                            icon: 'info',
                            showCancelButton: true,
                            confirmButtonText: 'Sí, volver!'
                        }).then((result) => {
                            if (result.isConfirmed) {
                                setStateEditor({
                                    ...stateEditor,
                                    editor: false,
                                    editorID: null,
                                });
                            } else {
                                setStateEditor({
                                    ...stateEditor,
                                    editorID: response.data.id,
                                });
                            }
                        });
                    }
                })
                .catch(error => {
                    console.error('Error modifying medio:', error.response?.data?.error || error.message);
                    showErrorAlert({
                        text: error.response?.data?.error || 'Ocurrió un error al modificar el medio.',
                        confirmButtonText: 'Entendido',
                    });
                });
        }
    };

    const handleDelete = async (event) => {
        event.preventDefault();
        try {
            const confirmed = await confirmDelete({
                title: '¿Quiere usted borrar este medio?',
                text: 'Esta acción no se puede revertir',
                confirmButtonText: 'Sí, borrar',
                cancelButtonText: 'Cancelar',
                reverseButtons: true,
            });
            if (!confirmed) return;

            try {
                await axios.delete(`media/${stateEditor.editorID}/`);
                await showAlert({ title: 'Medio borrado', icon: 'info' });
                setStateEditor({ ...stateEditor, editor: false, editorID: null });
            } catch (error) {
                console.error('Error deleting medio:', error.response?.data?.error || error.message);
                showErrorAlert({
                    text: error.response?.data?.error || 'Ocurrió un error al borrar el medio.',
                    confirmButtonText: 'Entendido',
                });
            }
        } catch (error) {
            console.error('Error al borrar el medio:', error);
            showErrorAlert({ text: 'Ocurrió un error inesperado.', confirmButtonText: 'Entendido' });
        }
    };

    return (
        <div className="h-full w-full">
            <form onSubmit={handleSubmit} className="flex flex-col md:flex-row landscape:flex-row h-full gap-3  w-full">
                {/* Área de Subida de Archivo */}
                <div className="flex justify-center items-center w-full
                          glassCard
                         md:w-2/5
                         landscape:flex-col landscape:w-2/5 landscape:h-full
                         h-1/2 md:h-full
                         flex-1
                         "

                >
                    <div className="w-full h-full p-4 rounded-lg relative">
                        <input
                            type="file"
                            id="file"
                            onChange={handleChangeSVG}
                            className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                            accept=".svg"
                        />
                        {file ? (
                            <img src={file} alt="Uploaded SVG" className="w-full h-full object-contain" />
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full">
                                <IconUpload className="w-10 h-10 text-secondary mb-2" />
                                <span className="text-secondary">Seleccionar archivo SVG</span>
                            </div>
                        )}
                        {errors.svg && <p className="text-error text-sm mt-2">{errors.svg}</p>}
                    </div>
                </div>

                {/* Formulario de Información del Medio */}
                <div className="flex flex-1 flex-col
                w-full md:w-3/5 space-y-4 glassCard p-4
                 h-1/2 md:h-full
                 landscape:h-full
                 landscape:w-3/5
                 overflow-y-scroll


                ">
                    {/* Nombre del Medio */}
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Nombre del medio*</span>
                        </label>
                        <input
                            name="nombre"
                            type="text"
                            value={medio.nombre}
                            onChange={handleChange}
                            className={`input input-bordered w-full ${errors.nombre ? 'input-error' : ''}`}
                            placeholder="Nombre del medio*"
                        />
                        {errors.nombre && (
                            <label className="label">
                                <span className="label-text-alt text-error">{errors.nombre}</span>
                            </label>
                        )}
                    </div>

                    {/* Permisos */}
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Permisos</span>
                        </label>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                            <label className="cursor-pointer label flex items-center">
                                <input
                                    type="radio"
                                    name="sharing_permissions"
                                    value="publico"
                                    checked={medio.sharing_permissions === 'publico'}
                                    onChange={handleChange}
                                    className="radio radio-primary mr-2"
                                />
                                <span className="label-text">Compartir con la comunidad</span>
                            </label>
                            <label className="cursor-pointer label flex items-center">
                                <input
                                    type="radio"
                                    name="sharing_permissions"
                                    value="privado"
                                    checked={medio.sharing_permissions === 'privado'}
                                    onChange={handleChange}
                                    className="radio radio-primary mr-2"
                                />
                                <span className="label-text">Privado</span>
                            </label>
                        </div>
                        {errors.sharing_permissions && (
                            <label className="label">
                                <span className="label-text-alt text-error">{errors.sharing_permissions}</span>
                            </label>
                        )}
                    </div>

                    {/* Botones de Acción */}
                    <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0">
                        {stateEditor.editorID && stateEditor.editorID !== 'add' && (
                            <button
                                onClick={handleDelete}
                                className="btn btn-error w-full sm:w-auto"
                            >
                                <Icon path={mdiDeleteOutline} size={1} /> Borrar Medio
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={!file}
                            className={`btn-secundario w-full sm:w-auto ${!file ? 'btn-disabled' : ''}`}
                        >
                            <Icon path={mdiContentSaveOutline} size={1} /> {stateEditor.editorID === 'add' ? 'Crear Medio' : 'Modificar Medio '}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );

};

export default MediosInputSVGEditor;
